import React, { useState, useEffect } from 'react';
import api, { 
  getMerchantBankAccount, 
  submitMerchantBankAccount, 
  submitWithdrawalRequest, 
  getWithdrawalRequests, 
  getMerchantDashboard 
} from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { showSuccessToast, showErrorToast } from '../../utils/toast';
import { formatDateDDMMYYYY, formatDateTimeDDMMYYYY } from '../../utils/dateUtils';
import { 
  Building, 
  User, 
  CreditCard, 
  FileText, 
  Calendar, 
  History, 
  Send, 
  CheckCircle2, 
  XCircle, 
  Clock, 
  ArrowUpRight,
  Info,
  ChevronLeft,
  ChevronRight,
  Eye
} from 'lucide-react';

const PAGE_SIZE = 10;

const STATUS_BADGES = {
  INITIATED: 'bg-blue-100 text-blue-800 border-blue-200',
  APPROVED: 'bg-amber-100 text-amber-800 border-amber-200',
  TRANSFERRED: 'bg-green-100 text-green-800 border-green-200',
  REJECTED: 'bg-red-100 text-red-800 border-red-200',
};

const BANK_STATUS_BADGES = {
  PENDING: 'bg-amber-100 text-amber-800 border-amber-200',
  APPROVED: 'bg-green-100 text-green-800 border-green-200',
  REJECTED: 'bg-red-100 text-red-800 border-red-200',
};

const Withdrawals = () => {
  const [bankAccount, setBankAccount] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [allRequests, setAllRequests] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submittingBank, setSubmittingBank] = useState(false);
  const [submittingWithdrawal, setSubmittingWithdrawal] = useState(false);

  // Bank Form State
  const [bankForm, setBankForm] = useState({
    accountHolderName: '',
    bankName: '',
    accountNumber: '',
    ifscCode: '',
    branchName: '',
  });
  const [cancelledCheque, setCancelledCheque] = useState(null);

  // Withdrawal Form State
  const [withdrawalAmount, setWithdrawalAmount] = useState('');

  // History Pagination & Dates
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  // Selected Request Detail Modal
  const [selectedRequest, setSelectedRequest] = useState(null);


  useEffect(() => {
    loadAllData();
  }, []);



  const loadAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchBankAccount(),
        fetchDashboardStats(),
        fetchHistory()
      ]);
    } catch (err) {
      console.error("Error loading withdrawal data:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchBankAccount = async () => {
    try {
      const res = await getMerchantBankAccount();
      setBankAccount(res.data || null);
      if (res.data) {
        setBankForm({
          accountHolderName: res.data.accountHolderName || '',
          bankName: res.data.bankName || '',
          accountNumber: res.data.accountNumber || '',
          ifscCode: res.data.ifscCode || '',
          branchName: res.data.branchName || '',
        });
      }
    } catch (err) {
      console.error("Error fetching bank account:", err);
      setBankAccount(null);
    }
  };

  const fetchDashboardStats = async () => {
    try {
      const res = await getMerchantDashboard();
      setDashboard(res.data || null);
    } catch (err) {
      console.error("Error fetching dashboard statistics:", err);
    }
  };

  const fetchHistory = async () => {
    try {
      // Fetch up to 1000 items for frontend paging (standard project behavior)
      const res = await getWithdrawalRequests(fromDate, toDate, 0, 1000);
      const data = res.data?.items || res.data || [];
      
      setAllRequests(data);
      setTotalPages(Math.ceil(data.length / PAGE_SIZE));
      setRequests(data.slice(0, PAGE_SIZE));
      setPage(0);
    } catch (err) {
      console.error("Error fetching history:", err);
      showErrorToast("Failed to load request history");
    }
  };

  const handleFilterHistory = (e) => {
    e.preventDefault();
    fetchHistory();
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    const start = newPage * PAGE_SIZE;
    setRequests(allRequests.slice(start, start + PAGE_SIZE));
  };

  // Submit Bank Account Details
  const handleBankSubmit = async (e) => {
    e.preventDefault();
    const { accountHolderName, bankName, accountNumber, ifscCode, branchName } = bankForm;

    if (!bankName.trim()) return showErrorToast("Bank Name is required");
    if (!accountNumber.trim()) return showErrorToast("Account Number is required");
    if (!ifscCode.trim()) return showErrorToast("IFSC Code is required");
    if (!cancelledCheque) return showErrorToast("Please upload a Cancelled Cheque");

    setSubmittingBank(true);
    try {
      const res = await submitMerchantBankAccount(
        { accountHolderName, bankName, accountNumber, ifscCode, branchName },
        cancelledCheque
      );
      showSuccessToast("Bank account submitted successfully for approval!");
      setBankAccount(res.data);
      setCancelledCheque(null);
      await fetchBankAccount();
    } catch (err) {
      let msg = "Failed to submit bank account details";
      if (err.response?.data?.message) msg = err.response.data.message;
      else if (err.message) msg = err.message;
      showErrorToast(msg);
    } finally {
      setSubmittingBank(false);
    }
  };

  // Submit Withdrawal Request
  const handleWithdrawalSubmit = async (e) => {
    e.preventDefault();
    if (!bankAccount || bankAccount.status !== 'APPROVED') {
      return showErrorToast("You must have an approved bank account to make withdrawals.");
    }

    const amt = parseFloat(withdrawalAmount);
    if (isNaN(amt) || amt <= 0) {
      return showErrorToast("Please enter a valid amount");
    }

    const usable = dashboard?.usableBalance || 0;
    if (amt > usable) {
      return showErrorToast(`Insufficient usable balance. Max requestable: ₹${usable.toLocaleString('en-IN')}`);
    }

    setSubmittingWithdrawal(true);
    try {
      await submitWithdrawalRequest({ amount: amt });
      showSuccessToast(`Withdrawal request of ₹${amt.toLocaleString()} submitted successfully!`);
      setWithdrawalAmount('');
      
      // Reload stats and history
      await Promise.all([
        fetchDashboardStats(),
        fetchHistory()
      ]);
    } catch (err) {
      let msg = "Failed to submit withdrawal request";
      if (err.response?.data?.message) msg = err.response.data.message;
      else if (err.message) msg = err.message;
      showErrorToast(msg);
    } finally {
      setSubmittingWithdrawal(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'TRANSFERRED':
        return <CheckCircle2 className="h-5 w-5 text-green-600" />;
      case 'REJECTED':
        return <XCircle className="h-5 w-5 text-red-600" />;
      case 'APPROVED':
        return <CheckCircle2 className="h-5 w-5 text-amber-600" />;
      default:
        return <Clock className="h-5 w-5 text-blue-600" />;
    }
  };

  if (loading && !bankAccount && !dashboard) {
    return (
      <div className="flex justify-center items-center py-16 gap-3">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        <span className="text-gray-600 font-medium">Loading withdrawal details...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Top Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="border border-indigo-100 shadow-md hover:shadow-xl transition-all duration-300 bg-gradient-to-br from-white to-indigo-50/40 overflow-hidden relative group">
          <div className="absolute top-0 right-0 -mr-8 -mt-8 w-32 h-32 rounded-full bg-indigo-100 opacity-40 group-hover:scale-150 transition-transform duration-700 ease-out" />
          <CardContent className="p-6 relative z-10 flex flex-col justify-between h-full">
            <div className="flex justify-between items-start mb-4">
              <span className="text-xs font-bold text-indigo-600 uppercase tracking-widest">Wallet Balance</span>
              <div className="p-2.5 bg-indigo-100 rounded-xl text-indigo-700 shadow-sm border border-indigo-200/50">
                <Building className="h-5 w-5" />
              </div>
            </div>
            <div>
              <h3 className="text-3xl font-extrabold text-slate-800 tracking-tight">₹{(dashboard?.walletBalance ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</h3>
              <div className="flex flex-wrap items-center justify-between gap-2 mt-2 pt-2 border-t border-indigo-100/60">
                <p className="text-xs text-slate-500 font-medium">Total balance in merchant wallet</p>
                <span className="text-xs font-bold text-amber-700 bg-amber-50/90 border border-amber-200/70 px-2.5 py-0.5 rounded-full shadow-xs">
                  Lien / Hold: ₹{(dashboard?.lienAmount ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-emerald-100 shadow-md hover:shadow-xl transition-all duration-300 bg-gradient-to-br from-white to-emerald-50/40 overflow-hidden relative group">
          <div className="absolute top-0 right-0 -mr-8 -mt-8 w-32 h-32 rounded-full bg-emerald-100 opacity-40 group-hover:scale-150 transition-transform duration-700 ease-out" />
          <CardContent className="p-6 relative z-10 flex flex-col justify-between h-full">
            <div className="flex justify-between items-start mb-4">
              <span className="text-xs font-bold text-emerald-600 uppercase tracking-widest">Usable Balance</span>
              <div className="p-2.5 bg-emerald-100 rounded-xl text-emerald-700 shadow-sm border border-emerald-200/50">
                <ArrowUpRight className="h-5 w-5" />
              </div>
            </div>
            <div>
              <h3 className="text-3xl font-extrabold text-slate-800 tracking-tight">₹{(dashboard?.usableBalance ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</h3>
              <p className="text-xs text-slate-500 mt-1.5 font-medium">Available for instant withdrawals</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="withdraw" className="w-full">
        <TabsList className="grid w-full grid-cols-3 max-w-lg bg-slate-100/80 p-1.5 rounded-2xl mb-8 shadow-inner border border-slate-200/50 h-auto">
          <TabsTrigger value="withdraw" className="rounded-xl py-2 text-sm font-bold transition-all data-[state=active]:shadow-md data-[state=active]:bg-white data-[state=active]:text-indigo-700">Request Withdrawal</TabsTrigger>
          <TabsTrigger value="bank" className="rounded-xl py-2 text-sm font-bold transition-all data-[state=active]:shadow-md data-[state=active]:bg-white data-[state=active]:text-indigo-700">Bank Account Details</TabsTrigger>
          <TabsTrigger value="history" className="rounded-xl py-2 text-sm font-bold transition-all data-[state=active]:shadow-md data-[state=active]:bg-white data-[state=active]:text-indigo-700">Request History</TabsTrigger>
        </TabsList>

        {/* ── Tab: Submit Withdrawal Request ── */}
        <TabsContent value="withdraw" className="space-y-6 outline-none">
          <Card className="shadow-xl border-slate-200/60 bg-white/90 backdrop-blur-sm rounded-2xl">
            <CardHeader className="pb-6 border-b border-slate-100">
              <CardTitle className="text-2xl font-bold text-slate-800">Request Withdrawal</CardTitle>
              <CardDescription className="text-slate-500 font-medium">Submit a withdrawal request. Funds will be directly transferred to your approved bank account.</CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              {(!bankAccount || bankAccount.status !== 'APPROVED') ? (
                <div className="flex flex-col items-center justify-center py-10 px-6 bg-slate-50 border-2 border-dashed border-slate-200 rounded-2xl w-full text-center space-y-5">
                  <div className="p-4 bg-amber-100 text-amber-600 rounded-full shadow-sm">
                    <Info className="h-8 w-8" />
                  </div>
                  <div className="space-y-2">
                    <h4 className="text-lg font-bold text-slate-800">Approved Bank Account Required</h4>
                    <p className="text-sm text-slate-500 max-w-sm leading-relaxed">
                      {!bankAccount 
                        ? "You have not submitted a bank account yet. Please navigate to the 'Bank Account Details' tab to register one."
                        : bankAccount.status === 'PENDING'
                        ? "Your bank account details are currently pending review. Withdrawals will be enabled once approved by admin."
                        : `Your bank account was rejected. Reason: ${bankAccount.rejectionReason || 'No details provided'}. Please submit updated details.`
                      }
                    </p>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleWithdrawalSubmit} className="w-full space-y-8 py-2">
                  <div className="relative overflow-hidden bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-100/80 p-5 rounded-2xl flex items-center gap-4 shadow-sm group">
                    <div className="absolute -right-4 -bottom-4 opacity-10 transform rotate-12 transition-transform duration-500 group-hover:rotate-6">
                       <Building className="h-24 w-24 text-emerald-600" />
                    </div>
                    <div className="h-12 w-12 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 shadow-inner border border-emerald-200/50">
                      <CheckCircle2 className="h-6 w-6 text-emerald-600" />
                    </div>
                    <div className="relative z-10">
                      <h4 className="font-bold text-emerald-950 text-sm tracking-wide">Destination Bank Account</h4>
                      <p className="text-xs text-emerald-800/80 mt-1 font-medium">
                        <span className="font-bold text-emerald-900">{bankAccount.bankName}</span> • A/C: <span className="font-mono font-bold text-emerald-950">••••{bankAccount.accountNumber?.slice(-4)}</span>
                      </p>
                      <p className="text-[10px] uppercase tracking-wider font-bold text-emerald-700/60 mt-0.5">{bankAccount.accountHolderName}</p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <Label htmlFor="amount" className="text-sm font-bold text-slate-700">Withdrawal Amount (₹) *</Label>
                    <div className="relative group/input">
                      <div className="absolute -inset-0.5 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-xl opacity-0 group-focus-within/input:opacity-20 transition duration-300 blur"></div>
                      <div className="relative flex items-center">
                        <span className="absolute left-5 text-slate-400 font-bold text-xl">₹</span>
                        <Input
                          id="amount"
                          type="number"
                          placeholder="0.00"
                          min="1"
                          step="any"
                          value={withdrawalAmount}
                          onChange={(e) => setWithdrawalAmount(e.target.value)}
                          className="pl-10 py-7 text-2xl font-extrabold text-slate-800 bg-slate-50 border-slate-200 rounded-xl focus-visible:ring-indigo-500 focus-visible:bg-white transition-all shadow-inner"
                          required
                        />
                      </div>
                    </div>
                    <p className="text-xs font-medium flex justify-between px-1">
                      <span className="text-slate-500">Maximum requestable: <strong className="text-slate-700">₹{(dashboard?.usableBalance || 0).toLocaleString('en-IN')}</strong></span>
                      {withdrawalAmount && parseFloat(withdrawalAmount) > 0 && (
                        <span className="text-indigo-600 font-bold animate-pulse">Wallet will be debited on submit</span>
                      )}
                    </p>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full py-7 text-base font-bold bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500 text-white shadow-xl shadow-indigo-200/50 rounded-xl transition-all duration-300 transform hover:-translate-y-0.5 flex items-center justify-center gap-2"
                    disabled={submittingWithdrawal}
                  >
                    {submittingWithdrawal ? (
                      <span className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                    ) : (
                      <Send className="h-5 w-5" />
                    )}
                    Submit Request
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Tab: Bank Account details / setup ── */}
        <TabsContent value="bank" className="space-y-6 outline-none">
          {bankAccount && bankAccount.status !== 'REJECTED' ? (
            <Card className="shadow-md border-slate-100">
              <CardHeader className="flex flex-row items-center justify-between border-b border-slate-50 pb-4">
                <div>
                  <CardTitle className="text-lg">Bank Account for Payouts</CardTitle>
                  <CardDescription>Status and details of your registered bank account</CardDescription>
                </div>
                <Badge className={`px-3 py-1 border text-xs font-semibold uppercase rounded-full ${BANK_STATUS_BADGES[bankAccount.status] || ''}`}>
                  {bankAccount.status}
                </Badge>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider block">Account Holder Name</label>
                      <span className="text-sm font-semibold text-gray-800 block mt-1">{bankAccount.accountHolderName || '—'}</span>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider block">Bank Name</label>
                      <span className="text-sm font-semibold text-gray-800 block mt-1">{bankAccount.bankName}</span>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider block">Account Number</label>
                      <span className="text-sm font-bold text-gray-800 block mt-1">•••• {bankAccount.accountNumber?.slice(-4) || '—'}</span>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider block">IFSC Code</label>
                      <span className="text-sm font-bold text-gray-800 block mt-1">{bankAccount.ifscCode}</span>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider block">Branch Name</label>
                      <span className="text-sm font-semibold text-gray-800 block mt-1">{bankAccount.branchName || '—'}</span>
                    </div>
                  </div>
                </div>

                {bankAccount.adminComment && (
                  <div className="mt-6 p-4 bg-slate-50 border border-slate-100 rounded-xl">
                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider block">Admin Comments</span>
                    <p className="text-sm text-slate-700 mt-1">{bankAccount.adminComment}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card className="shadow-md border-slate-100">
              <CardHeader>
                <CardTitle className="text-xl">Register Payout Bank Account</CardTitle>
                <CardDescription>Submit bank account details along with a cancelled cheque copy for verification.</CardDescription>
              </CardHeader>
              <CardContent>
                {bankAccount?.status === 'REJECTED' && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-xl text-red-800 text-sm">
                    <strong className="font-semibold block mb-0.5">Previous Submission Rejected</strong>
                    Reason: {bankAccount.rejectionReason || bankAccount.adminComment || 'No remarks provided.'}
                  </div>
                )}

                <form onSubmit={handleBankSubmit} className="space-y-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="space-y-1.5">
                      <Label htmlFor="accountHolderName" className="text-sm font-semibold text-gray-700">Account Holder Name</Label>
                      <Input
                        id="accountHolderName"
                        placeholder="Name as in bank record"
                        value={bankForm.accountHolderName}
                        onChange={(e) => setBankForm({ ...bankForm, accountHolderName: e.target.value })}
                        className="border-slate-200"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="bankName" className="text-sm font-semibold text-gray-700">Bank Name *</Label>
                      <Input
                        id="bankName"
                        placeholder="e.g. HDFC Bank, ICICI Bank"
                        value={bankForm.bankName}
                        onChange={(e) => setBankForm({ ...bankForm, bankName: e.target.value })}
                        className="border-slate-200"
                        required
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="accountNumber" className="text-sm font-semibold text-gray-700">Account Number *</Label>
                      <Input
                        id="accountNumber"
                        placeholder="Enter bank account number"
                        type="password"
                        value={bankForm.accountNumber}
                        onChange={(e) => setBankForm({ ...bankForm, accountNumber: e.target.value })}
                        className="border-slate-200 font-mono"
                        required
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="ifscCode" className="text-sm font-semibold text-gray-700">IFSC Code *</Label>
                      <Input
                        id="ifscCode"
                        placeholder="11 character alphanumeric code"
                        value={bankForm.ifscCode}
                        onChange={(e) => setBankForm({ ...bankForm, ifscCode: e.target.value.toUpperCase() })}
                        className="border-slate-200 font-mono"
                        required
                      />
                    </div>
                    <div className="space-y-1.5 md:col-span-2">
                      <Label htmlFor="branchName" className="text-sm font-semibold text-gray-700">Branch Name</Label>
                      <Input
                        id="branchName"
                        placeholder="Branch location name"
                        value={bankForm.branchName}
                        onChange={(e) => setBankForm({ ...bankForm, branchName: e.target.value })}
                        className="border-slate-200"
                      />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <Label htmlFor="chequeFile" className="text-sm font-semibold text-gray-700">Upload Cancelled Cheque / Passbook Copy *</Label>
                      <Input
                        id="chequeFile"
                        type="file"
                        accept="image/jpeg,image/png,application/pdf"
                        onChange={(e) => setCancelledCheque(e.target.files[0])}
                        className="border-slate-200 cursor-pointer file:bg-slate-50 file:border-0 file:py-1 file:px-3 file:rounded-md file:text-xs file:font-semibold"
                        required
                      />
                      <span className="text-xs text-gray-400 block mt-1">Allowed formats: JPG, PNG, PDF. Max file size: 5MB.</span>
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full mt-4 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3 rounded-lg shadow transition-colors"
                    disabled={submittingBank}
                  >
                    {submittingBank ? "Submitting details..." : "Submit Bank Details for Approval"}
                  </Button>
                </form>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ── Tab: Withdrawal Request History ── */}
        <TabsContent value="history" className="space-y-6 outline-none">
          <Card className="shadow-md border-slate-100">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg">Withdrawal Requests History</CardTitle>
              <CardDescription>Filter and track your wallet withdrawal requests status.</CardDescription>
            </CardHeader>
            <CardContent>
              {/* Date Filters */}
              <form onSubmit={handleFilterHistory} className="flex flex-wrap gap-4 items-end mb-6 bg-slate-50 p-4 rounded-xl border border-slate-100">
                <div className="space-y-1.5">
                  <Label htmlFor="fromDate" className="text-xs font-semibold text-slate-500 uppercase">From Date</Label>
                  <Input
                    id="fromDate"
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    className="border-slate-200 bg-white"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="toDate" className="text-xs font-semibold text-slate-500 uppercase">To Date</Label>
                  <Input
                    id="toDate"
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    className="border-slate-200 bg-white"
                  />
                </div>
                <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700 text-white">
                  Filter
                </Button>
                {(fromDate || toDate) && (
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => { setFromDate(''); setToDate(''); setTimeout(() => fetchHistory()); }}
                    className="border-slate-200"
                  >
                    Reset
                  </Button>
                )}
              </form>

              {/* Table */}
              <div className="overflow-x-auto rounded-xl border border-slate-100">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-100 text-xs font-semibold text-slate-500 uppercase">
                      <th className="p-4">Request ID / Date</th>
                      <th className="p-4">Requested Amt</th>
                      <th className="p-4">Charge</th>
                      <th className="p-4">Net Payout</th>
                      <th className="p-4">Status</th>
                      <th className="p-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-sm">
                    {requests.map((req) => (
                      <tr key={req.id} className="hover:bg-slate-50 transition-colors">
                        <td className="p-4">
                          <div className="font-semibold text-gray-800">{req.requestId}</div>
                          <div className="text-xs text-gray-400 mt-0.5">{formatDateTimeDDMMYYYY(req.createdAt)}</div>
                        </td>
                        <td className="p-4 font-bold text-gray-800">₹{req.amount?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                        <td className="p-4 text-gray-600">₹{req.charge?.toLocaleString('en-IN', { minimumFractionDigits: 2 }) || '0.00'}</td>
                        <td className="p-4 font-bold text-indigo-700">₹{req.netTransferAmount?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                        <td className="p-4">
                          <Badge className={`px-2.5 py-0.5 border text-xs font-semibold uppercase rounded-full ${STATUS_BADGES[req.status] || ''}`}>
                            {req.status}
                          </Badge>
                        </td>
                        <td className="p-4 text-right">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 font-semibold"
                            onClick={() => setSelectedRequest(req)}
                          >
                            <Eye className="h-4 w-4 mr-1" /> View Details
                          </Button>
                        </td>
                      </tr>
                    ))}
                    {requests.length === 0 && (
                      <tr>
                        <td colSpan="6" className="p-8 text-center text-gray-400">
                          No withdrawal requests found for the selected period.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-between items-center mt-6">
                  <span className="text-xs text-slate-500 font-medium">
                    Showing Page {page + 1} of {totalPages}
                  </span>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(page - 1)}
                      disabled={page === 0}
                      className="h-8 w-8 p-0"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(page + 1)}
                      disabled={page === totalPages - 1}
                      className="h-8 w-8 p-0"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ── Dialog: Request Details & History ── */}
      <Dialog open={!!selectedRequest} onOpenChange={() => setSelectedRequest(null)}>
        <DialogContent className="max-w-lg bg-white rounded-xl shadow-lg border border-slate-100">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-gray-900">Withdrawal Request Details</DialogTitle>
            <DialogDescription className="text-xs text-gray-400">Request: {selectedRequest?.requestId}</DialogDescription>
          </DialogHeader>

          {selectedRequest && (
            <div className="space-y-6 pt-2">
              <div className="flex justify-between items-center border-b border-slate-50 pb-4">
                <div>
                  <span className="text-xs text-slate-400 uppercase font-semibold">Status</span>
                  <div className="flex items-center gap-1.5 mt-1 font-bold">
                    {getStatusIcon(selectedRequest.status)}
                    <span className="text-sm uppercase tracking-wide">{selectedRequest.status}</span>
                  </div>
                </div>
                <div className="text-right">
                  <span className="text-xs text-slate-400 uppercase font-semibold">Net Transfer Amount</span>
                  <div className="text-lg font-bold text-indigo-600 mt-1">₹{selectedRequest.netTransferAmount?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-x-4 gap-y-3.5 text-sm">
                <div>
                  <span className="text-xs text-slate-400 block">Requested Amount</span>
                  <span className="font-semibold text-gray-800">₹{selectedRequest.amount?.toLocaleString('en-IN')}</span>
                </div>
                <div>
                  <span className="text-xs text-slate-400 block">Transfer Charge</span>
                  <span className="font-semibold text-gray-800">₹{selectedRequest.charge?.toLocaleString('en-IN') || '0.00'}</span>
                </div>
                <div>
                  <span className="text-xs text-slate-400 block">Balance Before Request</span>
                  <span className="font-semibold text-gray-800">₹{selectedRequest.balanceBefore?.toLocaleString('en-IN') || '—'}</span>
                </div>
                <div>
                  <span className="text-xs text-slate-400 block">Balance After Request</span>
                  <span className="font-semibold text-gray-800">₹{selectedRequest.balanceAfter?.toLocaleString('en-IN') || '—'}</span>
                </div>
                <div>
                  <span className="text-xs text-slate-400 block">Requested At</span>
                  <span className="font-semibold text-gray-800">{formatDateTimeDDMMYYYY(selectedRequest.createdAt)}</span>
                </div>
                <div>
                  <span className="text-xs text-slate-400 block">Transfer UTR</span>
                  <span className="font-mono font-bold text-gray-800">{selectedRequest.utr || '—'}</span>
                </div>
                {selectedRequest.transferDate && (
                  <div>
                    <span className="text-xs text-slate-400 block">Transfer Date</span>
                    <span className="font-semibold text-gray-800">{formatDateDDMMYYYY(selectedRequest.transferDate)}</span>
                  </div>
                )}
                {selectedRequest.rejectionReason && (
                  <div className="col-span-2 p-3 bg-red-50 border border-red-100 rounded-xl text-red-800 text-xs">
                    <strong>Rejection Reason:</strong> {selectedRequest.rejectionReason}
                  </div>
                )}
              </div>

              {/* Status Audit History */}
              {selectedRequest.history && selectedRequest.history.length > 0 && (
                <div className="border-t border-slate-50 pt-4">
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-3">Status Log History</span>
                  <div className="space-y-3 max-h-36 overflow-y-auto pr-1">
                    {selectedRequest.history.map((log, idx) => (
                      <div key={idx} className="flex gap-2.5 items-start text-xs border-l-2 border-slate-100 pl-3 ml-1">
                        <div className="min-w-0 flex-1">
                          <p className="text-gray-500">
                            Changed from <strong className="uppercase">{log.fromStatus || 'INITIATED'}</strong> to <strong className="uppercase">{log.toStatus}</strong>
                          </p>
                          {log.comment && <p className="text-gray-700 italic mt-0.5">"{log.comment}"</p>}
                          <p className="text-[10px] text-gray-400 mt-1">By {log.actor} · {formatDateTimeDDMMYYYY(log.createdAt)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Withdrawals;
